import * as Blockly from "blockly";
import { pythonGenerator, Order } from "blockly/python";

export const PROGRAM_COLOUR = "#5b67ca";

const TYPE_LABEL = { number: "number", text: "text", number_list: "list of numbers", text_list: "list of text" };

// The hat block is re-defined per question so its label lists that question's inputs
export const defineProgramBlocks = (inputs = []) => {
  const summary = inputs.length ? inputs.map((i) => `${i.name} (${TYPE_LABEL[i.type] || i.type})`).join(", ") : "no inputs";
  Blockly.Blocks["program_start"] = {
    init() {
      this.jsonInit({
        type: "program_start",
        message0: `when the program runs %1 inputs: ${summary.replace(/%/g, "%%")} %2 %3`,
        args0: [{ type: "input_dummy" }, { type: "input_dummy" }, { type: "input_statement", name: "BODY" }],
        colour: PROGRAM_COLOUR,
        tooltip: "Your program starts here. Put your blocks inside.",
        hat: "cap",
      });
      this.setDeletable(false);
      this.contextMenu = false;
    },
  };

  // "input n · number": the dropdown picks the input, the grey label shows its type so students know what they hold
  const typeOf = Object.fromEntries(inputs.map((i) => [i.name, TYPE_LABEL[i.type] || i.type]));
  Blockly.Blocks["program_input"] = {
    init() {
      const options = inputs.length ? inputs.map((i) => [i.name, i.name]) : [["(no inputs)", "__none__"]];
      this.jsonInit({
        type: "program_input",
        message0: "input %1 %2",
        args0: [
          { type: "field_dropdown", name: "NAME", options },
          { type: "field_label", name: "TYPE", text: typeOf[options[0][1]] ? `· ${typeOf[options[0][1]]}` : "" },
        ],
        output: null,
        colour: PROGRAM_COLOUR,
        tooltip: "A value given to the program - the grey word is its type (number, text, list)",
      });
      this.getField("NAME").setValidator((value) => {
        this.setFieldValue(typeOf[value] ? `· ${typeOf[value]}` : "", "TYPE");
        return value;
      });
    },
  };

  if (!Blockly.Blocks["text_to_number"]) {
    Blockly.defineBlocksWithJsonArray([
      {
        type: "lists_append",
        message0: "to list %1 add item %2",
        args0: [{ type: "field_variable", name: "VAR", variable: "list" }, { type: "input_value", name: "ITEM" }],
        previousStatement: null,
        nextStatement: null,
        colour: "#745ba5",
        tooltip: "Add an item to the end of a list (an empty list is created if the variable isn't one yet)",
        inputsInline: true,
      },
      {
        type: "text_to_number",
        message0: "number from text %1",
        args0: [{ type: "input_value", name: "TEXT" }],
        output: "Number",
        colour: "#5c81a6",
        tooltip: "Turn text like \"42\" into the number 42 (blank text becomes 0)",
      },
      {
        type: "number_to_text",
        message0: "text from %1",
        args0: [{ type: "input_value", name: "VALUE" }],
        output: "String",
        colour: "#5ba58c",
        tooltip: "Turn a number (or anything else) into text so it can be joined with other text",
      },
    ]);
  }

  if (!Blockly.Blocks["program_answer"]) {
    Blockly.defineBlocksWithJsonArray([
      {
        type: "program_answer",
        message0: "answer %1",
        args0: [{ type: "input_value", name: "VALUE" }],
        previousStatement: null,
        nextStatement: null,
        colour: PROGRAM_COLOUR,
        tooltip: "Print the answer (one line of output)",
      },
      {
        type: "program_say",
        message0: "say %1",
        args0: [{ type: "input_value", name: "VALUE" }],
        previousStatement: null,
        nextStatement: null,
        colour: PROGRAM_COLOUR,
        tooltip: "Print a line of text",
      },
    ]);
  }
};

const NUM_FN = [
  "def %FN%(s):",
  "  s = s.strip()",
  "  if s == '':",
  "    return 0",
  "  try:",
  "    return int(s)",
  "  except ValueError:",
  "    return float(s)",
];

// str() that prints whole floats as integers (5.0 -> "5"), matching how answers are printed
const TEXT_FN = [
  "def %FN%(v):",
  "  if isinstance(v, float) and v.is_integer():",
  "    v = int(v)",
  "  return str(v)",
];

const ANSWER_FN = [
  "def %FN%(v):",
  "  if isinstance(v, float) and v.is_integer():",
  "    v = int(v)",
  "  if isinstance(v, list):",
  "    print(' '.join(str(int(x)) if isinstance(x, float) and x.is_integer() else str(x) for x in v))",
  "  else:",
  "    print(v)",
];

const provide = (generator, name, lines) =>
  generator.provideFunction_(name, lines.map((l) => l.replace("%FN%", generator.FUNCTION_NAME_PLACEHOLDER_)));

// Bebras-style inputs: read stdin according to the question's schema into named variables
const inputAssignments = (inputs, numFn) => {
  const out = [];
  let i = 0;
  while (i < inputs.length) {
    const group = [inputs[i]];
    while (i + group.length < inputs.length && inputs[i + group.length].same_line) group.push(inputs[i + group.length]);
    if (group.length > 1) {
      out.push("_tokens = input().split()");
      group.forEach((inp, j) => out.push(`${inp.name} = ${inp.type === "number" ? `${numFn}(_tokens[${j}])` : `_tokens[${j}]`}`));
    } else {
      const inp = group[0];
      if (inp.type === "number") out.push(`${inp.name} = ${numFn}(input())`);
      else if (inp.type === "text") out.push(`${inp.name} = input()`);
      else if (inp.separator === "line") {
        const conv = inp.type === "number_list" ? `${numFn}(input())` : "input()";
        out.push(`${inp.name} = []`, "while True:", "  try:", `    ${inp.name}.append(${conv})`, "  except EOFError:", "    break");
      } else if (inp.type === "number_list") out.push(`${inp.name} = [${numFn}(t) for t in input().split()]`);
      else out.push(`${inp.name} = input().split()`);
    }
    i += group.length;
  }
  return out;
};

export const installGenerators = (inputs = []) => {
  pythonGenerator.forBlock["program_start"] = function (block, generator) {
    const needsNum = inputs.some((i) => i.type === "number" || i.type === "number_list");
    const numFn = needsNum ? provide(generator, "number_from_text", NUM_FN) : null;
    const body = generator.statementToCode(block, "BODY");
    const reads = inputAssignments(inputs, numFn);
    const dedented = body.replace(/^ {2}/gm, "");
    return `${reads.join("\n")}${reads.length ? "\n" : ""}${dedented}`;
  };
  pythonGenerator.forBlock["program_input"] = function (block) {
    const name = block.getFieldValue("NAME");
    return [name === "__none__" ? "None" : name, Order.ATOMIC];
  };
  pythonGenerator.forBlock["program_answer"] = function (block, generator) {
    const fn = provide(generator, "answer", ANSWER_FN);
    const v = generator.valueToCode(block, "VALUE", Order.NONE) || "None";
    return `${fn}(${v})\n`;
  };
  pythonGenerator.forBlock["program_say"] = function (block, generator) {
    const v = generator.valueToCode(block, "VALUE", Order.NONE) || "''";
    return `print(${v})\n`;
  };
  pythonGenerator.forBlock["lists_append"] = function (block, generator) {
    const name = generator.getVariableName(block.getFieldValue("VAR"));
    const item = generator.valueToCode(block, "ITEM", Order.NONE) || "None";
    return `if not isinstance(${name}, list):\n  ${name} = []\n${name}.append(${item})\n`;
  };
  // Friendlier than Blockly's default (which would print "None" in front of the first appended text)
  pythonGenerator.forBlock["text_append"] = function (block, generator) {
    const name = generator.getVariableName(block.getFieldValue("VAR"));
    const text = generator.valueToCode(block, "TEXT", Order.NONE) || "''";
    return `${name} = ('' if ${name} is None else str(${name})) + str(${text})\n`;
  };
  pythonGenerator.forBlock["text_to_number"] = function (block, generator) {
    const fn = provide(generator, "number_from_text", NUM_FN);
    const v = generator.valueToCode(block, "TEXT", Order.NONE) || "''";
    return [`${fn}(str(${v}))`, Order.FUNCTION_CALL];
  };
  pythonGenerator.forBlock["number_to_text"] = function (block, generator) {
    const fn = provide(generator, "text_from_value", TEXT_FN);
    const v = generator.valueToCode(block, "VALUE", Order.NONE) || "''";
    return [`${fn}(${v})`, Order.FUNCTION_CALL];
  };
};

// Generate Python for the program hat (plus any function definitions the student built; other orphan blocks are ignored)
export const workspaceToPython = (workspace, hatType = "program_start") => {
  const start = workspace.getBlocksByType(hatType, false)[0];
  if (!start) return "";
  pythonGenerator.init(workspace);
  workspace.getTopBlocks(true).forEach((b) => {
    if (b.type.startsWith("procedures_def") && b.isEnabled()) pythonGenerator.blockToCode(b);
  });
  let code = pythonGenerator.blockToCode(start);
  if (Array.isArray(code)) code = code[0];
  return pythonGenerator.finish(code).replace(/^\n+/, "").replace(/\n{3,}/g, "\n\n").trimEnd() + "\n";
};

// Every Blocks-mode category and its blocks (ids mirror backend utils/block_rules.py)
export const BLOCKS_CATEGORIES = [
  { id: "program", name: "Program", colour: PROGRAM_COLOUR, blocks: [
    { type: "program_input", label: "input" }, { type: "program_answer", label: "answer" }, { type: "program_say", label: "say" },
  ] },
  { id: "maths", name: "Maths", colour: "#5c81a6", blocks: [
    { type: "math_number", label: "number" }, { type: "math_arithmetic", label: "+ − × ÷ ^" }, { type: "math_single", label: "square root / abs / …" },
    { type: "math_round", label: "round" }, { type: "math_modulo", label: "remainder of" }, { type: "math_number_property", label: "is even / odd / prime…" },
    { type: "math_random_int", label: "random integer" }, { type: "math_on_list", label: "sum / min / max of list" },
    { type: "text_to_number", label: "number from text" },
  ] },
  { id: "text", name: "Text", colour: "#5ba58c", blocks: [
    { type: "text", label: "text" }, { type: "text_join", label: "join text" }, { type: "text_length", label: "length of text" },
    { type: "text_isEmpty", label: "text is empty" }, { type: "text_indexOf", label: "find in text" }, { type: "text_charAt", label: "letter at" },
    { type: "text_getSubstring", label: "part of text" }, { type: "text_changeCase", label: "UPPER / lower case" }, { type: "text_trim", label: "trim spaces" },
    { type: "text_count", label: "count occurrences" }, { type: "text_replace", label: "replace" }, { type: "text_reverse", label: "reverse text" },
    { type: "number_to_text", label: "text from number" }, { type: "text_append", label: "append text to variable" },
  ] },
  { id: "logic", name: "Logic", colour: "#5b80a5", blocks: [
    { type: "controls_if", label: "if / if-else" }, { type: "logic_compare", label: "compare (= ≠ < >)" }, { type: "logic_operation", label: "and / or" },
    { type: "logic_negate", label: "not" }, { type: "logic_boolean", label: "true / false" }, { type: "logic_ternary", label: "test ? if true : if false" },
  ] },
  { id: "loops", name: "Loops", colour: "#5ba55b", blocks: [
    { type: "controls_repeat_ext", label: "repeat N times" }, { type: "controls_whileUntil", label: "repeat while / until" }, { type: "controls_for", label: "count with" },
    { type: "controls_forEach", label: "for each item" }, { type: "controls_flow_statements", label: "break / continue" },
  ] },
  { id: "lists", name: "Lists", colour: "#745ba5", blocks: [
    { type: "lists_create_with", label: "create list" }, { type: "lists_append", label: "add item to list" }, { type: "lists_repeat", label: "list of repeated item" }, { type: "lists_length", label: "length of list" },
    { type: "lists_isEmpty", label: "list is empty" }, { type: "lists_indexOf", label: "find in list" }, { type: "lists_getIndex", label: "get item" },
    { type: "lists_setIndex", label: "set item" }, { type: "lists_getSublist", label: "sub-list" }, { type: "lists_split", label: "split text / join list" },
    { type: "lists_sort", label: "sort" }, { type: "lists_reverse", label: "reverse list" },
  ] },
  { id: "variables", name: "Variables", colour: "#a55b80", custom: "CD_VARIABLE", blocks: [
    { type: "variables_set", label: "set variable to" }, { type: "math_change", label: "change variable by" }, { type: "variables_get", label: "variable value" },
  ] },
  { id: "functions", name: "Functions", colour: "#995ba5", custom: "CD_PROCEDURE", blocks: [
    { type: "procedures_defnoreturn", label: "define function" }, { type: "procedures_defreturn", label: "define function with return" },
    { type: "procedures_ifreturn", label: "if … return" }, { type: "procedures_callnoreturn", label: "call function" }, { type: "procedures_callreturn", label: "call function (value)" },
  ] },
];

const BLOCK_PRESETS = {
  program_say: { inputs: { VALUE: { shadow: { type: "text", fields: { TEXT: "Hello!" } } } } },
  math_number: { fields: { NUM: 0 } },
  text: { fields: { TEXT: "Hello!" } },
  text_append: { inputs: { TEXT: { shadow: { type: "text", fields: { TEXT: "!" } } } } },
  controls_repeat_ext: { inputs: { TIMES: { shadow: { type: "math_number", fields: { NUM: 10 } } } } },
  controls_for: { inputs: { FROM: { shadow: { type: "math_number", fields: { NUM: 1 } } }, TO: { shadow: { type: "math_number", fields: { NUM: 10 } } }, BY: { shadow: { type: "math_number", fields: { NUM: 1 } } } } },
};
const toolboxBlock = (type) => (type === "controls_if"
  ? [{ kind: "block", type }, { kind: "block", type, extraState: { hasElse: true } }]
  : [{ kind: "block", type, ...(BLOCK_PRESETS[type] || {}) }]);

export const blocksBannedBlocks = (rules = {}) => {
  const exCats = new Set(rules.excluded_categories || []);
  const banned = new Set(rules.excluded_blocks || []);
  BLOCKS_CATEGORIES.filter((c) => exCats.has(c.id)).forEach((c) => c.blocks.forEach((b) => banned.add(b.type)));
  return banned;
};

// Toolbox minus whatever the question hides ("input" only appears when the question has inputs)
export const buildToolbox = (hasInputs, rules = {}) => {
  const exCats = new Set(rules?.excluded_categories || []);
  const exBlocks = new Set(rules?.excluded_blocks || []);
  if (!hasInputs) exBlocks.add("program_input");
  const contents = BLOCKS_CATEGORIES.filter((c) => !exCats.has(c.id)).map((c) => {
    const allowed = c.blocks.filter((b) => !exBlocks.has(b.type));
    if (!allowed.length) return null;
    if (c.custom) return { kind: "category", name: c.name, colour: c.colour, custom: c.custom };
    return { kind: "category", name: c.name, colour: c.colour, contents: allowed.flatMap((b) => toolboxBlock(b.type)) };
  }).filter(Boolean);
  return { kind: "categoryToolbox", contents };
};

const themes = {};
export const getTheme = (dark) => {
  if (!themes.light) {
    themes.light = Blockly.Theme.defineTheme("cd_light", {
      name: "cd_light",
      base: Blockly.Themes.Zelos,
      componentStyles: {
        workspaceBackgroundColour: "#f8fafc",
        toolboxBackgroundColour: "#f1f5f9",
        toolboxForegroundColour: "#0f172a",
        flyoutBackgroundColour: "#e2e8f0",
        flyoutForegroundColour: "#0f172a",
        flyoutOpacity: 0.95,
        scrollbarColour: "#94a3b8",
        insertionMarkerColour: "#0f172a",
        insertionMarkerOpacity: 0.3,
      },
      fontStyle: { family: "'Nunito', 'Segoe UI', sans-serif", weight: "600", size: 12 },
    });
    themes.dark = Blockly.Theme.defineTheme("cd_dark", {
      name: "cd_dark",
      base: Blockly.Themes.Zelos,
      componentStyles: {
        workspaceBackgroundColour: "#0b1220",
        toolboxBackgroundColour: "#111a2e",
        toolboxForegroundColour: "#e2e8f0",
        flyoutBackgroundColour: "#162036",
        flyoutForegroundColour: "#e2e8f0",
        flyoutOpacity: 0.96,
        scrollbarColour: "#475569",
        insertionMarkerColour: "#e2e8f0",
        insertionMarkerOpacity: 0.3,
      },
      fontStyle: { family: "'Nunito', 'Segoe UI', sans-serif", weight: "600", size: 12 },
    });
  }
  return dark ? themes.dark : themes.light;
};

// Default program: the hat with an empty "answer" block inside
export const defaultWorkspaceState = () => ({
  blocks: {
    languageVersion: 0,
    blocks: [
      {
        type: "program_start", x: 24, y: 24,
        inputs: { BODY: { block: { type: "program_answer" } } },
      },
    ],
  },
});
